import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import RepeatIcon from '@mui/icons-material/Repeat'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { resolveTaskSeries } from 'services/taskService'
import { PRIVATE_PATHS } from 'utils/paths'
import { SkeletonLine } from 'components/Skeleton'

const TaskSeriesRedirect = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [error, setError] = useState('')

  useEffect(() => {
    if (!id) return
    let cancelled = false

    resolveTaskSeries(id)
      .then(response => {
        if (cancelled) return
        const task = response?.data?.data || response?.data
        if (!task?.id) {
          setError('We could not find this repeating job.')
          return
        }
        navigate(PRIVATE_PATHS.taskDetails.replace(':id', task.id), { replace: true })
      })
      .catch(requestError => {
        if (cancelled) return
        setError(
          requestError?.response?.status === 403
            ? 'You do not have access to this repeating job. Ask whoever manages this building to add you.'
            : 'We could not find this repeating job. It may have been deleted.'
        )
      })

    return () => { cancelled = true }
  }, [id, navigate])

  return (
    <Wrapper>
      <Card>
        {error ? (
          <>
            <IconWrap $error><ErrorOutlineIcon /></IconWrap>
            <Heading>This code did not open a job</Heading>
            <Text>{error}</Text>
            <BackButton type='button' onClick={() => navigate(PRIVATE_PATHS.taskManagement)}>
              Go to my tasks
            </BackButton>
          </>
        ) : (
          <>
            <IconWrap><RepeatIcon /></IconWrap>
            <Heading>Opening the round that is due now…</Heading>
            <Text>
              This code belongs to a repeating job, so we are finding the right one for you.
            </Text>
            <SkeletonStack>
              <SkeletonLine $w='70%' $h={14} />
              <SkeletonLine $w='45%' $h={14} />
            </SkeletonStack>
          </>
        )}
      </Card>
    </Wrapper>
  )
}

export default TaskSeriesRedirect

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 460px;
  padding: 32px 24px;
  text-align: center;
  background: #fff;
  border: 1px solid #e6e6ea;
  border-radius: 12px;
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $error }) => ($error ? '#fdecea' : '#e6f7f9')};
  color: ${({ $error }) => ($error ? '#b00020' : '#33becc')};
`

const Heading = styled.h2`
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 700;
  color: #1c1c22;
`

const Text = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #5f5f6a;
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
`

const BackButton = styled.button`
  margin-top: 8px;
  min-height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
`
